/**
 * PhotosPage — family-wide media library at /photos.
 *
 * Shows all photos and videos uploaded by every family member,
 * grouped by month, with server-side filtering and pagination.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Search, Users, Play, Tag, X } from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { axiosInstance } from '../services/api';
import PhotoTagModal from '../components/media/PhotoTagModal';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const AVATAR_COLORS = ['#556B2F', '#8E6B23', '#3B72C4', '#6B3FA0', '#E65100', '#7B6B8E'];
const avatarColor = (name) => AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length];

function groupByMonth(items) {
  const groups = {};
  items.forEach(item => {
    const d = new Date(item.captured_at || item.uploaded_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!groups[key]) groups[key] = { label, items: [] };
    groups[key].items.push(item);
  });
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

export default function PhotosPage() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [totalVideos, setTotalVideos] = useState(0);
  const [uploaders, setUploaders] = useState([]);
  const [needsTaggingCount, setNeedsTaggingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [familyName, setFamilyName] = useState('');

  // Filters
  const [filterUploader, setFilterUploader] = useState(null);
  const [filterNeedsTagging, setFilterNeedsTagging] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Tag modal
  const [tagModal, setTagModal] = useState(null); // { items, index, total }

  const buildParams = useCallback((extraCursor) => {
    const params = new URLSearchParams({ limit: '36' });
    if (extraCursor) params.set('cursor', extraCursor);
    if (filterUploader) params.set('owner_id', filterUploader);
    if (filterNeedsTagging) params.set('needs_tagging', 'true');
    if (searchText.trim()) params.set('q', searchText.trim());
    return params;
  }, [filterUploader, filterNeedsTagging, searchText]);

  const fetchItems = useCallback(async (append = false, cursorOverride = null) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = buildParams(cursorOverride);
      const r = await axiosInstance.get(`/media/files/family-photos/?${params}`);
      const data = r.data;
      if (append) {
        setItems(prev => [...prev, ...data.items]);
      } else {
        setItems(data.items);
      }
      setCursor(data.nextCursor);
      setTotalPhotos(data.totalPhotos);
      setTotalVideos(data.totalVideos);
    } catch { /* ignore */ }
    setLoading(false);
    setLoadingMore(false);
  }, [buildParams]);

  // Load uploaders + family name
  useEffect(() => {
    axiosInstance.get('/media/files/family-uploaders/').then(r => {
      setUploaders(r.data.uploaders || []);
      setNeedsTaggingCount(r.data.needs_tagging || 0);
    }).catch(() => {});
    // Get family name
    const famId = localStorage.getItem('familyId');
    if (famId) {
      axiosInstance.get(`/account/families/detailed/`).then(r => {
        const fam = (r.data || []).find(f => f.id === famId);
        if (fam) setFamilyName(fam.name || '');
      }).catch(() => {});
    }
  }, []);

  // Fetch on filter change
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const loadMore = () => { if (cursor) fetchItems(true, cursor); };

  const openTagModal = (idx) => {
    setTagModal({ items, index: idx, total: totalPhotos + totalVideos });
  };

  const handleTagsChanged = (photoId, tags) => {
    setItems(prev => prev.map(p => p.id === photoId ? { ...p, tags } : p));
  };

  const handleDismissTagging = async (photoId) => {
    try {
      await axiosInstance.post(`/media/files/${photoId}/tag-dismissed/`);
      setItems(prev => prev.map(p => p.id === photoId ? { ...p, tagging_dismissed: true } : p));
      setNeedsTaggingCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const monthGroups = groupByMonth(items);
  const hasMore = !!cursor;

  return (
    <HubLayout activeView="photos">
      <div className="pp-content" style={{ maxWidth: 1040, margin: '0 auto', padding: '34px 32px 90px' }}>

        {/* Header */}
        <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 30, color: '#2C2F26', margin: '0 0 4px' }}>
          Photos & Media
        </h1>
        <p style={{ fontSize: 15, color: '#828A76', fontFamily: FONT_B, margin: '0 0 24px' }}>
          Everything the {familyName || 'family'} has uploaded &middot; {totalPhotos} photo{totalPhotos !== 1 ? 's' : ''} &middot; {totalVideos} video{totalVideos !== 1 ? 's' : ''}
        </p>

        {/* Filter row */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200,
            background: '#fff', border: '1.5px solid #DCD9C8', borderRadius: 12,
            padding: '9px 14px',
          }}>
            <Search size={16} color="#9AA088" />
            <input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search photos..."
              style={{
                border: 'none', outline: 'none', background: 'transparent',
                fontSize: 15, fontFamily: FONT_B, color: '#2C2F26', flex: 1,
              }}
            />
          </div>

          {/* Needs tagging pill */}
          {needsTaggingCount > 0 && (
            <button
              onClick={() => setFilterNeedsTagging(!filterNeedsTagging)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: filterNeedsTagging ? '#E65100' : '#FDEEE4',
                color: filterNeedsTagging ? '#fff' : '#C0552E',
                border: 'none', borderRadius: 999, padding: '8px 16px',
                fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
                minHeight: 40,
              }}
            >
              <Tag size={14} />
              Needs tagging &middot; {needsTaggingCount}
              {filterNeedsTagging && <X size={14} style={{ marginLeft: 4 }} />}
            </button>
          )}
        </div>

        {/* Uploader chips */}
        {uploaders.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
            <button
              onClick={() => setFilterUploader(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 14px', borderRadius: 999, cursor: 'pointer',
                border: !filterUploader ? '2px solid #556B2F' : '1.5px solid #DCD9C8',
                background: !filterUploader ? '#F0F5E8' : '#fff',
                fontSize: 13, fontWeight: 600, fontFamily: FONT_B, color: '#2C2F26',
              }}
            >Everyone</button>
            {uploaders.map(u => {
              const active = filterUploader === u.id;
              return (
                <button key={u.id}
                  onClick={() => setFilterUploader(active ? null : u.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px 6px 6px', borderRadius: 999, cursor: 'pointer',
                    border: active ? '2px solid #556B2F' : '1.5px solid #DCD9C8',
                    background: active ? '#F0F5E8' : '#fff',
                    fontSize: 13, fontWeight: 600, fontFamily: FONT_B, color: '#2C2F26',
                  }}
                >
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%', background: avatarColor(u.name),
                    color: '#fff', fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{u.initial}</div>
                  {u.name}
                  <span style={{ color: '#9AA088', fontWeight: 400 }}>{u.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#828A76', fontFamily: FONT_B }}>
            Loading media...
          </div>
        )}

        {/* Empty */}
        {!loading && items.length === 0 && (
          <div style={{
            textAlign: 'center', padding: 40, background: '#fff',
            borderRadius: 16, border: '1px solid #E4E1D2',
          }}>
            <p style={{ fontFamily: FONT_H, fontSize: 18, fontWeight: 600, color: '#556B2F', margin: '0 0 6px' }}>
              No photos or videos yet
            </p>
            <p style={{ color: '#828A76', fontSize: 15, margin: 0 }}>
              {filterNeedsTagging ? 'All photos have been tagged or dismissed.' : 'Upload photos and videos when creating stories.'}
            </p>
          </div>
        )}

        {/* Month groups */}
        {!loading && monthGroups.map(([key, group]) => (
          <div key={key} style={{ marginBottom: 32 }}>
            <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26', margin: '0 0 12px' }}>
              {group.label}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 10,
            }}>
              {group.items.map((item, i) => {
                const globalIdx = items.findIndex(it => it.id === item.id);
                const tagCount = item.tags?.length || 0;
                const needsTag = !item.tagging_dismissed && tagCount === 0;
                return (
                  <button key={item.id}
                    onClick={() => openTagModal(globalIdx)}
                    style={{
                      position: 'relative', aspectRatio: '1', borderRadius: 11,
                      overflow: 'hidden', border: 'none', padding: 0, cursor: 'pointer',
                      background: '#EFEEE5',
                    }}
                  >
                    <img src={item.url} alt="" style={{
                      width: '100%', height: '100%', objectFit: 'cover',
                    }} loading="lazy" />
                    {/* Video badge */}
                    {item.is_video && (
                      <div style={{
                        position: 'absolute', top: 6, right: 6,
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'rgba(28,32,22,.7)', backdropFilter: 'blur(4px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Play size={12} color="#fff" style={{ marginLeft: 1 }} />
                      </div>
                    )}
                    {/* Tag count badge */}
                    {tagCount > 0 && (
                      <div style={{
                        position: 'absolute', bottom: 6, left: 6,
                        background: 'rgba(28,32,22,.72)', color: '#fff',
                        borderRadius: 999, padding: '2px 8px', fontSize: 11,
                        fontWeight: 700, fontFamily: FONT_B,
                        display: 'flex', alignItems: 'center', gap: 3,
                      }}>
                        <Users size={10} /> {tagCount}
                      </div>
                    )}
                    {/* Needs tagging badge */}
                    {needsTag && (
                      <div style={{
                        position: 'absolute', bottom: 6, right: 6,
                        background: '#E65100', color: '#fff',
                        borderRadius: 999, padding: '2px 8px', fontSize: 11,
                        fontWeight: 700, fontFamily: FONT_B,
                      }}>Tag</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Load more */}
        {hasMore && !loading && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button onClick={loadMore} disabled={loadingMore} style={{
              background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 15,
              padding: '12px 28px', borderRadius: 999, border: 'none',
              cursor: loadingMore ? 'wait' : 'pointer', fontFamily: FONT_B,
              opacity: loadingMore ? 0.7 : 1, minHeight: 44,
            }}>
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>

      {/* Tag modal */}
      {tagModal && (
        <PhotoTagModal
          photos={items}
          initialIndex={tagModal.index}
          total={tagModal.total}
          onClose={() => setTagModal(null)}
          onLoadMore={async () => {
            if (!cursor) return items;
            const params = buildParams(cursor);
            const r = await axiosInstance.get(`/media/files/family-photos/?${params}`);
            const merged = [...items, ...r.data.items];
            setItems(merged);
            setCursor(r.data.nextCursor);
            return merged;
          }}
          onTagsChanged={handleTagsChanged}
          onDismissTagging={handleDismissTagging}
        />
      )}

      <style>{`
        @media (max-width: 640px) {
          .pp-content { padding: 16px 16px 60px !important; }
        }
      `}</style>
    </HubLayout>
  );
}
